import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'

import InvoiceType from '~/constants/InvoiceType'
import type { InvoiceOrEstimate } from '~/types'

import {
  PAYMENT_REQUEST_SEND_ERROR_KINDS,
  type PaymentRequestDialogContextType,
  type PaymentRequestDialogStateType,
  PaymentRequestFlowState,
  type PaymentRequestOnSendStubSuccess,
  type PaymentRequestSendErrorKind,
  type PaymentRequestSendSuccessPayload,
} from '../constants/paymentRequestDialogV2Constants'
import { formatSendToDisplay } from '../utils/paymentRequestDialogV2Utils'

export const PaymentRequestDialogContext =
  createContext<PaymentRequestDialogContextType | null>(null)

export const initialDialogState: PaymentRequestDialogStateType = {
  clinicNotes: '',
  deliveryMethod: 'email',
  flowState: PaymentRequestFlowState.REQUEST_FORM,
  sendTo: [],
  successPayload: null,
}

export interface PaymentRequestDialogProviderProps {
  children: ReactNode
  invoice: InvoiceOrEstimate
  /**
   * Optional hook after send succeeds (state becomes `SUCCESS` with `successPayload`).
   * TODO(CVC-36749): implement `emailPayment` (or equivalent) inside `sendPayment`; set `SUCCESS` only on API success.
   */
  onSendStubSuccess?: PaymentRequestOnSendStubSuccess
  /**
   * Until real API: delayed stub resolves (success UI) or rejects (error banner).
   */
  sendPaymentStubOutcome?: 'success' | 'failure'
}

export const PaymentRequestDialogProvider = ({
  children,
  invoice,
  onSendStubSuccess,
  // TODO(CVC-36749): remove default value once real API is implemented
  sendPaymentStubOutcome = 'failure',
}: PaymentRequestDialogProviderProps) => {
  const [dialogState, setDialogState] =
    useState<PaymentRequestDialogStateType>(initialDialogState)
  const [sendError, setSendError] =
    useState<PaymentRequestSendErrorKind | null>(null)

  const dialogStateRef = useRef(dialogState)
  dialogStateRef.current = dialogState

  const resetDialogState = useCallback(() => {
    setSendError(null)
    setDialogState((prev) => ({
      ...prev,
      clinicNotes: '',
      flowState: PaymentRequestFlowState.REQUEST_FORM,
      successPayload: null,
    }))
  }, [])

  const actions = useMemo(
    () => ({
      dismissSendError: () => setSendError(null),
      resetDialogState,
      setClinicNotes: (clinicNotes: string) => {
        setDialogState((prev) => ({ ...prev, clinicNotes }))
      },
      setDeliveryMethod: (
        method: PaymentRequestDialogStateType['deliveryMethod'],
      ) => {
        setDialogState((prev) => ({ ...prev, deliveryMethod: method }))
      },
      setSendTo: (recipients: PaymentRequestDialogStateType['sendTo']) => {
        setDialogState((prev) => ({ ...prev, sendTo: recipients }))
      },
      sendPayment: async () => {
        const prev = dialogStateRef.current
        if (prev.sendTo.length === 0) {
          return
        }
        if (
          prev.flowState === PaymentRequestFlowState.SENDING ||
          prev.flowState === PaymentRequestFlowState.SUCCESS
        ) {
          return
        }

        const { deliveryMethod, sendTo } = prev

        setSendError(null)
        setDialogState((p) => ({
          ...p,
          flowState: PaymentRequestFlowState.SENDING,
        }))

        try {
          // TODO(CVC-36749): replace with real API call; catch and map errors when schema is known
          await new Promise<void>((resolve, reject) => {
            setTimeout(() => {
              if (sendPaymentStubOutcome === 'failure') {
                reject(new Error('CONNECTION_ISSUE'))
              } else {
                resolve()
              }
            }, 2000)
          })

          const payload: PaymentRequestSendSuccessPayload = {
            isDeposit: invoice.type === InvoiceType.ESTIMATE,
            recipientDisplay: formatSendToDisplay(sendTo, deliveryMethod),
          }
          setDialogState((p) => ({
            ...p,
            flowState: PaymentRequestFlowState.SUCCESS,
            successPayload: payload,
          }))
          onSendStubSuccess?.(payload)
        } catch (error: unknown) {
          // TODO(CVC-36749): implement error mapping when schema is known
          setDialogState((p) => ({
            ...p,
            flowState: PaymentRequestFlowState.REQUEST_FORM,
            successPayload: null,
          }))
          const message =
            error instanceof Error ? error.message : 'CONNECTION_ISSUE'
          const kind = PAYMENT_REQUEST_SEND_ERROR_KINDS.includes(
            message as PaymentRequestSendErrorKind,
          )
            ? message
            : 'CONNECTION_ISSUE'
          setSendError(kind as PaymentRequestSendErrorKind)
        }
      },
    }),
    [invoice, onSendStubSuccess, resetDialogState, sendPaymentStubOutcome],
  )

  const contextValue = useMemo<PaymentRequestDialogContextType>(
    () => ({
      actions,
      dialogState,
      invoice,
      sendError,
    }),
    [actions, dialogState, invoice, sendError],
  )

  return (
    <PaymentRequestDialogContext.Provider value={contextValue}>
      {children}
    </PaymentRequestDialogContext.Provider>
  )
}

export const usePaymentRequestDialog = (): PaymentRequestDialogContextType => {
  const context = useContext(PaymentRequestDialogContext)
  if (!context) {
    throw new Error(
      'usePaymentRequestDialog must be used within a PaymentRequestDialogProvider',
    )
  }
  return context
}
